import React from 'react';
import {
  Table,
  TableBody,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Box,
  Chip,
  IconButton,
  Tooltip,
  Button
} from '@mui/material';
import {
  Edit as EditIcon,
  Business as BusinessIcon,
  Security as SecurityIcon,
  Key as KeyIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useLocale } from '../../context/LocaleContext';
import MultipleRoleSelector from './MultipleRoleSelector';
import { USER_ROLE_DISPLAY_NAMES } from '../../types/enums';

// Styled Components
const StyledTableContainer = styled(Box)(({ theme }) => ({
  borderRadius: '12px',
  overflow: 'hidden',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
  '& .MuiTable-root': {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  },
  '& .MuiTableCell-root': {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    fontSize: '0.875rem',
    letterSpacing: '0.01em',
  },
  '& .MuiTableHead .MuiTableCell-root': {
    fontWeight: 600,
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    fontSize: '0.875rem',
    letterSpacing: '0.01em',
    backgroundColor: theme.palette.grey[50],
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  '& .MuiTableBody .MuiTableCell-root': {
    fontWeight: 400,
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    fontSize: '0.875rem',
    letterSpacing: '0.01em',
    borderBottom: `1px solid ${theme.palette.divider}`,
  }
}));

const StyledTableCell = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5),
  fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  fontWeight: 600,
  fontSize: '0.875rem',
  letterSpacing: '0.01em',
  backgroundColor: theme.palette.grey[50],
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const StyledDataCell = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5),
  fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  fontWeight: 400,
  fontSize: '0.875rem',
  letterSpacing: '0.01em',
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const ModernChip = styled(Chip)(({ theme }) => ({
  borderRadius: '8px',
  fontWeight: 500,
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: '8px',
  textTransform: 'none',
  fontWeight: 600,
  minWidth: 'auto',
  marginRight: theme.spacing(1),
  padding: theme.spacing(0.5, 1.5),
  fontSize: '0.75rem'
}));

const UserManagementTable = ({
  users,
  filteredUsers,
  paginatedUsers,
  page,
  rowsPerPage,
  searchQuery,
  selectedUser,
  rolesUpdate,
  editingUser,
  showEditUser,
  onPageChange,
  onRowsPerPageChange,
  onRoleUpdate,
  onStartEditingRoles,
  onStartEditingUser,
  onAssignFacility,
  onPasswordReset,
  onDeactivation,
  getFacilityName,
  userRole,
  theme,
  setRolesUpdate,
  activeTab // Add activeTab prop to determine which columns to show
}) => {
  const { t } = useLocale();

  // Helper function to get chip colors based on role
  const getChipColor = (role) => {
    const colors = {
      admin: '#d32f2f',
      facility_admin: '#f57c00',
      doctor: '#1976d2',
      nurse: '#388e3c',
      user: '#616161',
      billing: '#9c27b0'
    };
    return colors[role] || '#616161';
  };

  // Helper function to render user roles
  const renderUserRoles = (user) => {
    let userRoles;
    if (user.role && Array.isArray(user.role)) {
      userRoles = user.role;
    } else if (user.roles && Array.isArray(user.roles)) {
      userRoles = user.roles;
    } else if (user.role && !Array.isArray(user.role)) {
      userRoles = [user.role].filter(Boolean);
    } else {
      userRoles = [];
    }
    
    if (userRoles.length === 0) return t('userManagement.roles.user');
    
    if (userRoles.length === 1) {
      return t(`userManagement.roles.${userRoles[0]}`);
    }

    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'center' }}>
        {userRoles.map((role, index) => (
          <Chip
            key={`${user.id}-role-${role}-${index}`}
            label={USER_ROLE_DISPLAY_NAMES[role] || role}
            size="small"
            sx={{ 
              bgcolor: getChipColor(role),
              color: 'white',
              fontSize: '0.75rem'
            }}
          />
        ))}
      </Box>
    );
  };

  console.log('UserManagementTable rendering with:', {
    usersLength: users.length,
    filteredUsersLength: filteredUsers.length,
    paginatedUsersLength: paginatedUsers.length,
    page,
    rowsPerPage,
    activeTab
  });

  // Determine if this is the patient portal users tab
  const isPatientPortalTab = activeTab === 1;

  return (
    <StyledTableContainer>
      
     
      
      <Table>
        <TableHead>
          <TableRow>
            <StyledTableCell component="th">{t('userManagement.name')}</StyledTableCell>
            <StyledTableCell component="th">{t('userManagement.email')}</StyledTableCell>
            {!isPatientPortalTab && <StyledTableCell component="th">NPI</StyledTableCell>}
            {!isPatientPortalTab && <StyledTableCell component="th">Taxonomy</StyledTableCell>}
            <StyledTableCell component="th">Phone</StyledTableCell>
            <StyledTableCell component="th">Default Facility</StyledTableCell>
            <StyledTableCell component="th">Status</StyledTableCell>
            {!isPatientPortalTab && <StyledTableCell component="th">{t('userManagement.assignedFacilities')}</StyledTableCell>}
            <StyledTableCell component="th">2FA</StyledTableCell>
            <StyledTableCell component="th">{t('userManagement.role')}</StyledTableCell>
            <StyledTableCell component="th" align="right">{t('userManagement.actions')}</StyledTableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {console.log('Table body - paginatedUsers:', paginatedUsers)}
          {console.log('Table body - paginatedUsers length:', paginatedUsers.length)}
          {paginatedUsers.length === 0 ? (
            <TableRow>
              <StyledDataCell component="td" colSpan={isPatientPortalTab ? 8 : 11} align="center">
                <Typography variant="body2" color="text.secondary">
                  No users found. Users: {users.length}, Filtered: {filteredUsers.length}, Paginated: {paginatedUsers.length}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Debug: searchQuery="{searchQuery}", page={page}, rowsPerPage={rowsPerPage}
                </Typography>
              </StyledDataCell>
            </TableRow>
          ) : (
            paginatedUsers.map((user, index) => {
              console.log('Rendering user row:', { user, index, userId: user.id });
              return (
                <TableRow 
                  key={user.id ? `user-${user.id}` : `user-index-${index}`}
                  sx={{ 
                    '&:hover': { 
                      backgroundColor: theme.palette.action.hover 
                    } 
                  }}
                >
                  <StyledDataCell component="td" sx={{ fontWeight: 500 }}>
                    {isPatientPortalTab && user.firstName && user.lastName 
                      ? `${user.firstName} ${user.lastName}` 
                      : user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim() || '-'}
                  </StyledDataCell>
                  <StyledDataCell component="td">{user.email}</StyledDataCell>
                  {!isPatientPortalTab && <StyledDataCell component="td">{user.npi || '-'}</StyledDataCell>}
                  {!isPatientPortalTab && <StyledDataCell component="td">{user.taxonomyCode || '-'}</StyledDataCell>}
                  <StyledDataCell component="td">{user.phone || '-'}</StyledDataCell>
                  <StyledDataCell component="td">{getFacilityName(user.defaultFacility)}</StyledDataCell>
                  <StyledDataCell component="td">
                    <ModernChip
                      size="small"
                      label={user.status === 'inactive' ? 'Inactive' : 'Active'}
                      sx={{
                        bgcolor: user.status === 'inactive' ? theme.palette.error.light : theme.palette.success.light,
                        color: 'white',
                        fontWeight: 500,
                      }}
                    />
                  </StyledDataCell>
                  {!isPatientPortalTab && (
                    <StyledDataCell component="td">
                      {user.facilities && user.facilities.length > 0 ? (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {user.facilities.slice(0, 2).map((facility, index) => (
                            <Chip 
                              key={`${user.id}-facility-${facility.id}-${index}`} 
                              label={facility.name || facility.id} 
                              size="small" 
                              sx={{ fontSize: '0.7rem' }}
                            />
                          ))}
                          {user.facilities.length > 2 && (
                            <Chip 
                              key={`${user.id}-facility-more`}
                              label={`+${user.facilities.length - 2} more`} 
                              size="small" 
                              variant="outlined"
                              sx={{ fontSize: '0.7rem' }}
                            />
                          )}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">-</Typography>
                      )}
                    </StyledDataCell>
                  )}
                  <StyledDataCell component="td">
                    <ModernChip
                      size="small"
                      label={user.twoFactorEnabled ? 'Enabled' : 'Disabled'}
                      sx={{
                        bgcolor: user.twoFactorEnabled ? theme.palette.success.light : theme.palette.grey[300],
                        color: user.twoFactorEnabled ? 'white' : theme.palette.text.secondary,
                      }}
                    />
                  </StyledDataCell>
                  <StyledDataCell component="td" align="center">
                    {selectedUser?.id === user.id ? (
                      <Box sx={{ minWidth: 300 }}>
                        <MultipleRoleSelector
                          selectedRoles={rolesUpdate}
                          onChange={(roles) => setRolesUpdate(roles)}
                          showLegacyWarning={!user.roles}
                          label="Update Roles"
                        />
                        <Box sx={{ mt: 1 }}>
                          <ActionButton 
                            size="small" 
                            variant="contained" 
                            color="primary"
                            onClick={() => onRoleUpdate(user.id, rolesUpdate)}
                            sx={{ mr: 1 }}
                          >
                            Save
                          </ActionButton>
                          <ActionButton 
                            size="small"
                            variant="outlined"
                            color="secondary"
                            onClick={() => {
                              // Handle cancel
                            }}
                          >
                            Cancel
                          </ActionButton>
                        </Box>
                      </Box>
                    ) : (
                      renderUserRoles(user)
                    )}
                  </StyledDataCell>
                  <StyledDataCell component="td" align="right">
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                      {!isPatientPortalTab && (
                        <Tooltip title={t('userManagement.assignFacility')} arrow>
                          <IconButton
                            size="small"
                            onClick={() => onAssignFacility(user)}
                            sx={{ 
                              color: theme.palette.primary.main,
                              '&:hover': { backgroundColor: theme.palette.primary.light + '20' }
                            }}
                          >
                            <BusinessIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      
                      {!isPatientPortalTab && (
                        <Tooltip title={t('userManagement.changeRole')} arrow>
                          <IconButton
                            size="small"
                            onClick={() => onStartEditingRoles(user)}
                            sx={{ 
                              color: theme.palette.secondary.main,
                              '&:hover': { backgroundColor: theme.palette.secondary.light + '20' }
                            }}
                          >
                            <SecurityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      
                      {!isPatientPortalTab && (
                        <Tooltip title={t('userManagement.edit')} arrow>
                          <IconButton
                            size="small"
                            onClick={() => onStartEditingUser(user)}
                            sx={{ 
                              color: theme.palette.info.main,
                              '&:hover': { backgroundColor: theme.palette.info.light + '20' }
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      
                      {userRole && userRole.includes('admin') && (
                        <Tooltip title="Reset Password" arrow>
                          <IconButton
                            onClick={() => onPasswordReset(user)}
                            sx={{ 
                              color: theme.palette.warning.main,
                              '&:hover': { backgroundColor: theme.palette.warning.light + '20' }
                            }}
                          >
                            <KeyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      
                      {userRole && userRole.includes('admin') && (
                        <Tooltip title={user.status === 'inactive' ? 'Activate User' : 'Deactivate User'} arrow>
                          <IconButton
                            onClick={() => onDeactivation(user)}
                            sx={{ 
                              color: user.status === 'inactive' ? theme.palette.error.main : theme.palette.success.main,
                              '&:hover': { 
                                backgroundColor: user.status === 'inactive' 
                                  ? theme.palette.error.light + '20' 
                                  : theme.palette.success.light + '20' 
                              }
                            }}
                          >
                            {user.status === 'inactive' ? '🚫' : '✅'}
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </StyledDataCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50]}
        component="div"
        count={filteredUsers.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
      />
    </StyledTableContainer>
  );
};

export default UserManagementTable;
